/**
 * <variant-picker>
 * ---------------------------------------------------------------------------
 * Selector de variantes del producto.
 *
 * Responsabilidades:
 *   - Traducir la selección de opciones (color, talla, modelo…) en una variante
 *   - Actualizar precio, disponibilidad, SKU y el input oculto del formulario
 *   - Reflejar la variante en la URL (?variant=) para que el enlace sea compartible
 *   - Avisar al resto de la ficha con el evento `variant:change`
 *
 * No toca la galería directamente: emite el evento y <product-gallery> reacciona.
 * Así cada componente hace una sola cosa y se pueden usar por separado.
 *
 * Sin JavaScript el formulario sigue funcionando: los inputs son radios reales
 * dentro de un <form> y hay un <select> de respaldo con todas las variantes.
 */

// Envuelto en IIFE: varias secciones cargan este archivo (la ficha de producto y
// el modal de compra rápida del grupo de superposiciones) y dos <script> con la
// misma URL se ejecutan dos veces. Sin este envoltorio, el segundo lanza
// "Identifier 'VariantPicker' has already been declared" al analizarse.
(() => {


class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.productUrl;
    // El modal de compra rápida se abre desde una colección o desde la portada:
    // ahí la URL es la del listado, no la de este producto, y tocarla dejaría al
    // cliente con un `?variant=` que no describe la página en la que está.
    this.updateUrlEnabled = this.dataset.updateUrl !== 'false';
    this.colorPosition = this.dataset.colorPosition ? Number(this.dataset.colorPosition) : null;

    this.variants = this.#readVariants();
    if (!this.variants.length) return;

    this.addEventListener('change', this.#onChange);

    // Estado inicial: marca como no disponibles las combinaciones imposibles
    this.#updateOptionAvailability();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  /** Lee el JSON de variantes que inyecta Liquid. */
  #readVariants() {
    const script = this.querySelector('[data-variant-data]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('[variant-picker] No se pudo leer los datos de variantes.', error);
      return [];
    }
  }

  /** Valores actualmente seleccionados, en orden de opción. */
  get selectedOptions() {
    return Array.from(this.querySelectorAll('[data-option-input]:checked')).map(
      (input) => input.value
    );
  }

  get currentVariant() {
    const selected = this.selectedOptions;
    return this.variants.find(
      (variant) =>
        variant.options.length === selected.length &&
        variant.options.every((option, index) => option === selected[index])
    );
  }

  #onChange = () => {
    const variant = this.currentVariant;

    this.#updateOptionAvailability();
    this.#updateSelectedLabels();
    this.#updateFormInput(variant);
    this.#updateUrl(variant);
    this.#updatePrice(variant);
    this.#updateBuyButton(variant);
    this.#announce(variant);
  };

  /**
   * Marca visualmente las opciones que no llevan a ninguna variante comprable.
   * No las deshabilita: el cliente puede seguir explorando combinaciones, que es
   * mejor UX que un control muerto sin explicación.
   */
  #updateOptionAvailability() {
    const selected = this.selectedOptions;

    this.querySelectorAll('[data-option-input]').forEach((input) => {
      const position = Number(input.dataset.optionPosition);
      const candidate = [...selected];
      candidate[position] = input.value;

      const match = this.variants.find((variant) =>
        variant.options.every((option, index) =>
          index === position ? option === input.value : option === candidate[index]
        )
      );

      const available = Boolean(match && match.available);
      input.closest('[data-option-item]')?.classList.toggle('is-unavailable', !available);
    });
  }

  /** Actualiza el "Color: Azul marino" que acompaña a cada grupo de opciones. */
  #updateSelectedLabels() {
    this.querySelectorAll('[data-option-group]').forEach((group) => {
      const checked = group.querySelector('[data-option-input]:checked');
      const output = group.querySelector('[data-option-selected-value]');
      if (checked && output) output.textContent = checked.value;
    });
  }

  #updateFormInput(variant) {
    const input = this.form?.querySelector('input[name="id"]');
    if (input && variant) input.value = variant.id;
  }

  /** Deep link: recargar o compartir la URL abre exactamente esta variante. */
  #updateUrl(variant) {
    if (!this.updateUrlEnabled) return;
    if (!variant || !this.productUrl) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  #updatePrice(variant) {
    const container = document.getElementById(`price-${this.sectionId}`);
    if (!container || !variant) return;

    // El bloque de precio se re-renderiza en el servidor: así el formato de
    // moneda, los impuestos y las reglas de descuento salen siempre correctos,
    // sin duplicar lógica de formateo en JavaScript.
    fetch(`${this.productUrl}?variant=${variant.id}&section_id=${this.sectionId}`)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.getElementById(`price-${this.sectionId}`);
        if (fresh) container.innerHTML = fresh.innerHTML;
      })
      .catch((error) => console.error('[variant-picker] No se pudo actualizar el precio.', error));
  }

  #updateBuyButton(variant) {
    // La PDP puede tener mas de un boton de compra (p.ej. "Comprar" y
    // "Añadir a la cesta"): hay que actualizar todos, no solo el primero.
    const buttons = this.form?.querySelectorAll('[data-add-to-cart]') ?? [];

    buttons.forEach((button) => {
      const label = button.querySelector('[data-add-to-cart-text]') ?? button;

      if (!variant) {
        button.disabled = true;
        label.textContent = button.dataset.labelUnavailable;
      } else if (!variant.available) {
        button.disabled = true;
        label.textContent = button.dataset.labelSoldOut;
      } else {
        button.disabled = false;
        label.textContent = button.dataset.labelAdd;
      }
    });
  }

  /** Avisa a la galería y a la capa de analítica. */
  #announce(variant) {
    const colorValue =
      this.colorPosition !== null ? this.selectedOptions[this.colorPosition] ?? '' : '';

    this.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: {
          variant,
          colorValue,
          colorHex: this.#currentColorHex(),
          selectedOptions: this.selectedOptions,
          sectionId: this.sectionId,
        },
      })
    );
  }

  /**
   * Hex de la muestra de color seleccionada, si la hay (ver data-hex en
   * snippets/color-swatch.liquid). Lo usa <product-photo-tint> para repintar
   * la foto comodín; el resto de componentes puede ignorarlo.
   */
  #currentColorHex() {
    if (this.colorPosition === null) return '';
    const group = this.querySelectorAll('[data-option-group]')[this.colorPosition];
    const swatch = group?.querySelector('[data-option-input]:checked ~ label [data-hex]');
    return swatch?.dataset.hex || '';
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
})();
